import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../../../utils/FirebaseConfig";
import {
    addArrayItem,
    buildHirarkiByRole,
    DEFAULT_CATS,
    normalizeCategoryKey,
    removeArrayItem,
    rolesFromCategory,
    titleCase,
    updateArrayItem,
} from "../utils/profailUtils";

/* ===== storage helpers ===== */
const extractStoragePathFromUrl = (url) => {
    try {
        const decoded = decodeURIComponent(url);
        const m = decoded.match(/\/o\/(.*?)\?/);
        return m ? m[1] : null;
    } catch {
        return null;
    }
};

const deleteByUrl = async (url) => {
    if (!url) return;
    const path = extractStoragePathFromUrl(url);
    if (!path) return;
    await deleteObject(ref(storage, path));
};

const resizeImage = (file, maxSize = 900, quality = 0.82) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
        const canvas = document.createElement("canvas");

        reader.onload = (e) => {
            img.onload = () => {
                const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);

                canvas.width = w;
                canvas.height = h;

                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);

                canvas.toBlob(
                    (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
                    "image/jpeg",
                    quality
                );
            };
            img.src = e.target.result;
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const uploadReplace = async ({ mosqueId, fileOrBlob, oldUrl, index }) => {
    try {
        await deleteByUrl(oldUrl);
    } catch { }

    const newPath = `${mosqueId}/profailImages/pegawai_${index}_${Date.now()}.jpg`;
    const r = ref(storage, newPath);
    await uploadBytes(r, fileOrBlob);
    return await getDownloadURL(r);
};

/* ===== group builder (view mode) ===== */
const buildPegawaiGroupsForView = (pegawaiList = []) => {
    const list = Array.isArray(pegawaiList) ? pegawaiList : [];
    const groupMap = new Map();

    const ensureGroup = (key, label) => {
        if (!groupMap.has(key)) groupMap.set(key, { key, label, items: [] });
        return groupMap.get(key);
    };

    ensureGroup("pengurusan", "PENGURUSAN");
    ensureGroup("imam", "IMAM");
    ensureGroup("bilal", "BILAL");

    // custom category labels
    list.forEach((p) => {
        const cat = normalizeCategoryKey(p.category);
        if (!DEFAULT_CATS.includes(cat)) {
            ensureGroup(cat, String(p.label || titleCase(cat)).toUpperCase());
        }
    });

    // fill groups
    list.forEach((p) => {
        const cat = normalizeCategoryKey(p.category);
        const roles = new Set(p.roles || rolesFromCategory(cat));
        const baseH = Number(p.hirarki ?? 99);

        const hirarkiByRole = buildHirarkiByRole({
            category: cat,
            roles: Array.from(roles),
            baseHirarki: baseH,
        });

        // group asal ikut category
        ensureGroup(cat, String(p.label || titleCase(cat)).toUpperCase()).items.push({
            ...p,
            __rank: baseH,
        });

        // duplicate display untuk imam/bilal jika ditanda
        if (roles.has("imam") && cat !== "imam") {
            ensureGroup("imam", "IMAM").items.push({ ...p, __rank: hirarkiByRole.imam });
        }
        if (roles.has("bilal") && cat !== "bilal") {
            ensureGroup("bilal", "BILAL").items.push({ ...p, __rank: hirarkiByRole.bilal });
        }
    });

    // sort by rank
    for (const g of groupMap.values()) {
        g.items.sort((a, b) => (a.__rank ?? 99) - (b.__rank ?? 99));
    }

    // order groups: base first, then customs (A-Z)
    const customs = [...groupMap.values()].filter(
        (g) => !DEFAULT_CATS.includes(g.key) && g.key !== "pengurusan"
    );
    customs.sort((a, b) => a.label.localeCompare(b.label));

    return [groupMap.get("pengurusan"), groupMap.get("imam"), groupMap.get("bilal"), ...customs].filter(Boolean);
};

export default function PegawaiSection({ mosqueId, editMode, draft, setDraft, cats, setCats }) {
    const [newCat, setNewCat] = useState("");

    const list = useMemo(() => {
        return Array.isArray(draft?.pegawai) ? draft.pegawai : [];
    }, [draft?.pegawai]);
    const setPegawai = (next) => setDraft((prev) => ({ ...prev, pegawai: next }));

    const addCategory = () => {
        const raw = String(newCat || "").trim();
        if (!raw) return;

        const normalized = normalizeCategoryKey(raw);
        if (!cats.includes(normalized)) setCats([...cats, normalized]);
        setNewCat("");
    };

    const addPegawai = () => {
        const next = addArrayItem(list, {
            id: crypto?.randomUUID?.() || String(Date.now() + Math.random()),
            category: "pengurusan",
            label: "Pengurusan",
            nama: "",
            jawatan: "",
            hirarki: 99,
            gambar: "",
            roles: ["pengurusan"],
        });
        setPegawai(next);
    };

    const removePegawai = async (index) => {
        const item = list[index];
        if (item?.gambar) {
            try {
                await deleteByUrl(item.gambar);
            } catch { }
        }
        setPegawai(removeArrayItem(list, index));
    };

    const change = (index, field, value) => {
        const current = list[index] || {};

        if (field === "category") {
            const cat = normalizeCategoryKey(value);
            const roles = rolesFromCategory(cat);
            const nextLabel = DEFAULT_CATS.includes(cat) ? titleCase(cat) : (current.label || titleCase(value));

            setPegawai(updateArrayItem(list, index, { category: cat, label: nextLabel, roles }));
            return;
        }

        setPegawai(updateArrayItem(list, index, { [field]: value }));
    };

    const toggleRole = (index, role, checked) => {
        const current = list[index] || {};
        const cat = normalizeCategoryKey(current.category);

        const roles = new Set(current.roles || rolesFromCategory(cat));
        if (checked) roles.add(role);
        else roles.delete(role);

        // lock untuk imam/bilal category
        if (cat === "imam") {
            roles.clear();
            roles.add("imam");
        } else if (cat === "bilal") {
            roles.clear();
            roles.add("bilal");
        } else {
            // pastikan pengurusan kekal untuk non-imam/bilal
            roles.add("pengurusan");
        }

        setPegawai(updateArrayItem(list, index, { roles: Array.from(roles) }));
    };

    const uploadImage = async (e, index) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const resized = await resizeImage(file, 900, 0.82);
            const oldUrl = list[index]?.gambar || "";
            const url = await uploadReplace({ mosqueId, fileOrBlob: resized, oldUrl, index });

            setPegawai(updateArrayItem(list, index, { gambar: url }));
            toast.success("Gambar pegawai berjaya dimuat naik");
        } catch (err) {
            console.error(err);
            toast.error("Gagal upload gambar pegawai");
        }
    };

    const viewGroups = useMemo(() => buildPegawaiGroupsForView(list), [list]);

    return (
        <div className="pegawai-section">
            {/* Panduan (dalam section yang sama) */}


            {editMode ? (
                <>
                    {/* add category */}
                    <div className="pegawai-row" style={{ alignItems: "stretch" }}>
                        <input
                            className="field-input"
                            placeholder="Tambah kategori (cth: ajk kariah)"
                            value={newCat}
                            onChange={(e) => setNewCat(e.target.value)}
                        />
                        <button type="button" className="filter-button" onClick={addCategory}>
                            + Kategori
                        </button>
                    </div>

                    {/* list */}
                    <div className="pegawai-list">
                        {list.map((p, index) => {
                            const cat = normalizeCategoryKey(p.category || "pengurusan");
                            const isPengurusanOrCustom = cat !== "imam" && cat !== "bilal";
                            const roles = new Set(p.roles || rolesFromCategory(cat));

                            return (
                                <div key={p.id || index} className="pegawai-edit-card">
                                    {/* ROW 1 */}
                                    <div className="pegawai-column">
                                        <select
                                            className="field-input"
                                            value={cat}
                                            onChange={(e) => change(index, "category", e.target.value)}
                                            style={{ minWidth: 180 }}
                                        >
                                            {cats.map((c) => (
                                                <option key={c} value={c}>
                                                    {c}
                                                </option>
                                            ))}
                                        </select>

                                        {!DEFAULT_CATS.includes(cat) && (
                                            <input
                                                className="field-input"
                                                placeholder="Label kategori (cth: AJK Kariah)"
                                                value={p.label || ""}
                                                onChange={(e) => change(index, "label", e.target.value)}
                                            />
                                        )}

                                        <input
                                            className="field-input"
                                            placeholder="Nama Pegawai"
                                            value={p.nama || ""}
                                            onChange={(e) => change(index, "nama", e.target.value)}
                                        />

                                        <input
                                            className="field-input"
                                            placeholder="Jawatan"
                                            value={p.jawatan || ""}
                                            onChange={(e) => change(index, "jawatan", e.target.value)}
                                        />
                                    </div>

                                    {/* ROW 2 */}
                                    <div className="pegawai-row pegawai-row-bottom">
                                        <input
                                            type="number"
                                            className="field-input"
                                            style={{ width: 120 }}
                                            value={p.hirarki ?? 99}
                                            onChange={(e) => change(index, "hirarki", e.target.value)}
                                        />

                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => uploadImage(e, index)}
                                            className="pegawai-file"
                                        />
                                        <div className="pv2-guide">
                                            <img className="pv2-guide-img" src="/images/pegawai.png" alt="Panduan gambar pegawai" />
                                            <div className="pv2-guide-note">
                                                • Paras pinggang hingga kepala<br />
                                                • Portrait (tegak), fokus di tengah
                                            </div>
                                        </div>

                                        {isPengurusanOrCustom && (
                                            <div className="pegawai-role-toggles">
                                                <label className="pv2-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={roles.has("imam")}
                                                        onChange={(e) => toggleRole(index, "imam", e.target.checked)}
                                                    />
                                                    Imam
                                                </label>

                                                <label className="pv2-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={roles.has("bilal")}
                                                        onChange={(e) => toggleRole(index, "bilal", e.target.checked)}
                                                    />
                                                    Bilal
                                                </label>
                                            </div>
                                        )}

                                        <button type="button" className="pv2-danger" onClick={() => removePegawai(index)}>
                                            Padam
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="button-container">
                        <button type="button" className="filter-button" onClick={addPegawai}>
                            + Tambah Pegawai
                        </button>
                    </div>
                </>
            ) : (
                <div className="pegawai-view">
                    {viewGroups.map((g) => (
                        <div key={g.key} className="pegawai-display">
                            <div className="pv2-group-title">{g.label}</div>

                            {g.items.length === 0 ? (
                                <div className="pv2-muted">-</div>
                            ) : (
                                <div className="pegawai-grid">
                                    {g.items.map((p, idx) => (
                                        <div key={p.id || idx} className="pegawai-card">
                                            <img
                                                src={p.gambar || "/images/pegawai.png"}
                                                alt={p.nama || ""}
                                                onError={(e) => (e.currentTarget.style.display = "none")}
                                            />
                                            <div style={{ fontWeight: 900, fontSize: 13 }}>{p.nama || "-"}</div>
                                            <div style={{ fontSize: 12, opacity: 0.75 }}>{p.jawatan || ""}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
